
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Task } from '@/types/lead';
import { getTasksByLead, createTask, completeTask, deleteTask, updateTask, getTaskPriorityOptions, getTaskCategoryOptions } from '@/services/leads/taskService';
import { logActivity } from '@/services/leads/activityService';
import { useAuth } from '@/context/AuthContext';

export const useTaskPanel = (leadId: string) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('medium');
  const [newTaskAssignedTo, setNewTaskAssignedTo] = useState('');
  const [newTaskCategories, setNewTaskCategories] = useState<string[]>([]);
  const [newTaskReminderTimes, setNewTaskReminderTimes] = useState<string[]>([]);
  
  const priorityOptions = getTaskPriorityOptions();
  const categoryOptions = getTaskCategoryOptions();

  useEffect(() => {
    fetchTasks();
  }, [leadId]);

  const fetchTasks = async () => {
    setIsLoading(true);
    try {
      console.log('[TaskPanel] Fetching tasks for leadId:', leadId);
      const tasksData = await getTasksByLead(leadId);
      console.log('[TaskPanel] Fetched tasks:', tasksData.length, 'tasks');
      console.log('[TaskPanel] Tasks data:', tasksData);
      setTasks(tasksData);
    } catch (error) {
      console.error('[TaskPanel] Error fetching tasks:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load tasks. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTask = async () => {
    try {
      // Ensure we have a session user and an assignee
      if (!user?.id) {
        toast({ variant: 'destructive', title: 'Not signed in', description: 'Please sign in again and retry.' });
        return;
      }
      const assignedToId = newTaskAssignedTo || user.id;
      if (!assignedToId) {
        toast({ variant: 'destructive', title: 'Missing assignee', description: 'Please select who the task is assigned to.' });
        return;
      }

      console.log('[TaskPanel] Creating task for leadId:', leadId);
      console.log('[TaskPanel] Task data:', {
        title: newTaskTitle,
        description: newTaskDescription,
        dueDate: newTaskDueDate,
        priority: newTaskPriority,
        assignedTo: assignedToId,
      });
      
      const createdTask = await createTask({
        leadId,
        title: newTaskTitle,
        description: newTaskDescription,
        dueDate: newTaskDueDate || undefined,
        priority: newTaskPriority as any,
        status: 'pending',
        assignedTo: assignedToId,
        categories: newTaskCategories,
        reminderTimes: newTaskReminderTimes.length > 0 ? newTaskReminderTimes : undefined,
      });
      
      console.log('[TaskPanel] Created task returned by API:', createdTask);
      
      // Optimistic update: show the task immediately
      setTasks(prev => [createdTask, ...prev]);
      
      // Fetch tasks again to refresh the list
      await fetchTasks();
      
      // Reset form
      setNewTaskTitle('');
      setNewTaskDescription('');
      setNewTaskDueDate('');
      setNewTaskPriority('medium');
      setNewTaskAssignedTo('');
      setNewTaskCategories([]);
      setNewTaskReminderTimes([]);
      setIsTaskDialogOpen(false);
      
      toast({
        title: "Task Created",
        description: "The task has been added successfully",
      });
    } catch (error: any) {
      console.error('[TaskPanel] Error adding task:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.message || error?.details || error?.hint || error?.error_description || 'Failed to create task',
      });
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      const updatedTask = await completeTask(taskId);
      
      setTasks(prev => 
        prev.map(task => task.id === taskId ? updatedTask : task)
      );
      
      await logActivity({
        leadId,
        type: 'task',
        title: `Task completed: ${updatedTask.title}`,
        description: updatedTask.description,
      });
      
      toast({
        title: "Task Completed",
        description: "The task has been marked as completed.",
      });
    } catch (error) {
      console.error('Error completing task:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to complete task. Please try again.",
      });
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await deleteTask(taskId);
      
      setTasks(prev => 
        prev.filter(task => task.id !== taskId)
      );
      
      toast({
        title: "Task Deleted",
        description: "The task has been removed.",
      });
    } catch (error) {
      console.error('Error deleting task:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete task. Please try again.",
      });
    }
  };

  const handleUpdateTask = async (taskId: string, updates: Partial<Task>) => {
    try {
      const updatedTask = await updateTask(taskId, updates);
      
      setTasks(prev => 
        prev.map(task => task.id === taskId ? updatedTask : task)
      );
      
      toast({
        title: "Task Updated",
        description: "The task has been updated successfully.",
      });
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update task. Please try again.",
      });
    }
  };

  const pendingTasks = tasks.filter(task => task.status === 'pending');
  const completedTasks = tasks.filter(task => task.status === 'completed');

  return {
    isLoading,
    tasks,
    pendingTasks,
    completedTasks,
    isTaskDialogOpen,
    setIsTaskDialogOpen,
    newTaskTitle,
    setNewTaskTitle,
    newTaskDescription,
    setNewTaskDescription,
    newTaskDueDate,
    setNewTaskDueDate,
    newTaskPriority,
    setNewTaskPriority,
    newTaskAssignedTo,
    setNewTaskAssignedTo,
    newTaskCategories,
    setNewTaskCategories,
    newTaskReminderTimes,
    setNewTaskReminderTimes,
    handleAddTask,
    handleCompleteTask,
    handleDeleteTask,
    handleUpdateTask,
    priorityOptions,
    categoryOptions,
  };
};
